import { ReactNode } from 'react';
import { ProjectionID } from '@views/CustomDashboards/types';

export type InitChatbotSessionRequest = {
  projectionId: ProjectionID;
  entityKey: string;
};

export interface InitChatbotSessionResponse {
  initChatbotSession: {
    data: { chatRunInfo: ChatRunInfo; defaultPrompts: string[]; useFallbackQueries: boolean };
    error: Omit<TCopilotError, 'screenType'>;
  };
}

export interface PublishChatbotMessageRequest {
  chatRunInfo: ChatRunInfo;
  userPrompt: string;
}
export interface GenerateChatbotSummaryRequest {
  chatRunInfo: ChatRunInfo;
}

export interface PublishChatbotMessageData {
  publishChatbotMessage: null;
}

export interface GenerateChatbotSummaryData {
  generateChatbotSummary: null;
}

export interface ChatbotEndSessionRequest {
  chatRunInfo: ChatRunInfo;
}

export interface EndChatbotSessionData {
  endChatbotSession: { data: boolean; error: Omit<TCopilotError, 'screenType'> };
}

export interface GetChatbotMessageResponse {
  getChatbotMessage: { data: { message: ChatMessage; status: ChatbotWfStatus }; error: Omit<TCopilotError, 'screenType'> };
}

export interface ChatRunInfo {
  threadId: string;
}

export enum CopilotGetStatusChatMessageType {
  ANSWER = 'ANSWER',
  SUMMARY = 'SUMMARY',
}
export enum AgentId {
  AGENT_ID_UNSPECIFIED = 'AGENT_ID_UNSPECIFIED',
  AGENT_QUERY_GENIE = 'AGENT_QUERY_GENIE',
  AQL_AGENT = 'AQL_AGENT',
  ENTITY_MAPPING_AGENT = 'ENTITY_MAPPING_AGENT',
  TICKET_AGENT = 'TICKET_AGENT',
  INCIDENT_RULE_GENERATION_AGENT = 'INCIDENT_RULE_GENERATION_AGENT',
  REMEDIATION_COPILOT = 'REMEDIATION_COPILOT',
}

export enum AgenticWorkflowMode {
  AGENTIC_WORKFLOW_MODE_UNSPECIFIED = 'AGENTIC_WORKFLOW_MODE_UNSPECIFIED',
  AGENTIC_WORKFLOW_MODE_INTERACTIVE = 'AGENTIC_WORKFLOW_MODE_INTERACTIVE',
  AGENTIC_WORKFLOW_MODE_AUTONOMOUS = 'AGENTIC_WORKFLOW_MODE_AUTONOMOUS',
}

export interface AgenticSessionInput {
  agentId: AgentId;
  workflowMode: AgenticWorkflowMode;
  additionalParams?: Record<string, any>;
}

export interface AgentResponse {
  getAgentStatus: {
    status: ChatbotStatus;
    result: AgentResult;
    timestamp: string;
  };
}

export type AgentResult = AgentSuccess | AgentError;

export interface AgentSuccess {
  __typename: 'AgentSuccess';
  content: string;
  structuredOutput: any;
}

export interface AgentError {
  __typename: 'AgentError';
  message: string;
}

export enum ChatbotStatus {
  UNSPECIFIED = 'UNSPECIFIED',
  READY = 'READY',
  THINKING = 'THINKING',
  LOADING_DATA = 'LOADING_DATA',
  TOOL_CALLING = 'TOOL_CALLING',
  DONE = 'DONE',
  SUMMARIZING = 'SUMMARIZING',
  ERROR = 'ERROR',
  TIMEOUT = 'TIMEOUT',
}

export enum CopilotChatMessageType {
  CONVERSATION_MESSAGE_TYPE_AI = 'CONVERSATION_MESSAGE_TYPE_AI',
  CONVERSATION_MESSAGE_TYPE_USER = 'CONVERSATION_MESSAGE_TYPE_USER',
  CONVERSATION_MESSAGE_TYPE_SUMMARY = 'CONVERSATION_MESSAGE_TYPE_SUMMARY',
  ERROR = 'ERROR',
}

export type ChatMessage = {
  type: CopilotChatMessageType;
  content: string;
  timestamp: string;
  structuredOutput?: any;
  state?: any;
  isError?: boolean;
  loopId?: string;
};

export type TCopilotError = { message: string; type: ChatbotErrorType };

export const enum ChatbotWfStatus {
  CHATBOT_UNSPECIFIED = 'CHATBOT_UNSPECIFIED',
  CHATBOT_READY = 'CHATBOT_READY',
  CHATBOT_THINKING = 'CHATBOT_THINKING',
  CHATBOT_DONE = 'CHATBOT_DONE',
  CHATBOT_SUMMARIZING = 'CHATBOT_SUMMARIZING',
  CHATBOT_ERROR = 'CHATBOT_ERROR',
  CHATBOT_TIMEOUT = 'CHATBOT_TIMEOUT',
}

export const enum ChatbotErrorType {
  NOT_FOUND = 'NOT_FOUND',
  INTERNAL = 'INTERNAL',
  QUOTA_EXCEEDED = 'QUOTA_EXCEEDED',
}

export type TCopilotActions = ((message: ChatMessage & { sessionId: string; chatRunInfo: ChatRunInfo }) => ReactNode)[];

type TGetConfirmDialogConfig = (lastMessage?: ChatMessage) => { title: string; body: string };

type TOnAgentResponse = (message: ChatMessage) => void;

export type TSessionProps = {
  actions: TCopilotActions;
  getConfirmDialogConfig?: TGetConfirmDialogConfig;
  retainSessionOnClose?: boolean;
  onAgentResponse?: TOnAgentResponse;
} & (
  | {
      agentId: AgentId.TICKET_AGENT;
      additionalParams: InitChatbotSessionRequest;
    }
  | {
      agentId: AgentId;
      additionalParams: Record<string, any>;
    }
);

export type UserPrompt = { sessionId: string; message: string; isStateUpdated?: boolean; stateUpdate?: TAgentState };

export type TAgentState = Record<string, unknown> | undefined;

export enum CustomOperationName {
  SUMMARIZE_CHAT = 'SUMMARIZE_CHAT',
}

export interface OperationInput {
  sessionId: string;
  operationName: CustomOperationName;
  data?: any;
}

export enum ReactionType {
  LIKED = 'THUMBS_UP',
  DISLIKED = 'THUMBS_DOWN',
}

export interface FeedbackInput {
  loopId: string;
  sessionId: string;
  reactionType: ReactionType;
  freeText?: string;
  categories?: string[];
}
